import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import store from 'shared/utils/localStore';
import Timer from './timer';
import { DarkBackground } from 'scy/Background';
import { TextButton } from 'scy/buttons';
import { SmallUppercase } from 'scy/text';
import { sansSerifMedium } from 'scy/text/styles.mod.scss';
import { LEAD_CAPTURE_CLOSED_AT_KEY } from 'components/login/actions';
import { loadPromos } from 'components/promo/actions';
import { firstPromoToApply } from 'shared/models/promo';
import { showAvailability } from 'components/availability/actions';
import { trackNow } from 'shared/models/analytic';
import { isEqual } from 'shared/models/address';
import config from 'sharedConfig';
import styles from './styles.mod.scss';

const TIMER_LENGTH = 20 * 60 * 1000; // 20 minutes

class Banner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      message: this._message(props),
      timerExpired: false
    };
  }

  _message(props, state = {}) {
    if (this.props.checkout) return this._checkoutMessage(props);

    if (!this._hasAddress(props.address)) return 'noAddress';
    if (this._outsideDelivery(props.vendor)) return 'outsideDelivery';
    if (this._hasPromo(props.user, props.promo)) return 'hasPromo';
    if (this._newCustomer(props.leadCaptureClosedAt, props.user, state))
      return 'newCustomer';
    if (this._shippingOnly(props.address, props.vendor)) return 'shippingOnly';
    return 'onDemandAvailable';
  }

  _checkoutMessage(props) {
    if (!this._hasAddress(props.address)) return null;
    if (this._outsideDelivery(props.checkoutVendor)) return 'outsideDelivery';
    if (this._shippingOnly(props.address, props.checkoutVendor))
      return 'shippingOnly';
    return 'onDemandAvailable';
  }

  _hasAddress(address) {
    return address && address.lat && address.lng;
  }

  _outsideDelivery(vendor) {
    if (!vendor) return;
    return !vendor.inside_delivery_zone && !vendor.shipping;
  }

  _hasPromo(user, promo) {
    if (!user) return;
    return !!this.availableCredit(promo);
  }

  _newCustomer(leadCaptureClosedAt, user, state = {}) {
    if (state.timerExpired) return;
    const registeredAt = this.userRegisteredAt(leadCaptureClosedAt, user);
    if (!registeredAt) return;
    const msAgo = new Date() - new Date(registeredAt);
    return msAgo < TIMER_LENGTH;
  }

  _shippingOnly(address, vendor) {
    if (!this._hasAddress(address)) return;
    if (!vendor) return;
    return !!vendor.shipping;
  }

  componentDidMount() {
    this._checkPromoCodes(this.props);

    const message = this.state.message;
    if (message) {
      if (this.props.onMessageChange) this.props.onMessageChange(message);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.user && this.props.user !== prevProps.user) {
      this._checkPromoCodes(this.props);
    }

    if (this._hasChanged(prevProps, prevState)) {
      this.setMessage(this._message(this.props, this.state));
    }

    if (this.state.message && this.state.message !== prevState.message) {
      this._track(this.state.message, this.props);
    }
  }

  setMessage(message) {
    this.setState({ message });
    if (this.props.onMessageChange) this.props.onMessageChange(message);
  }

  _checkPromoCodes(props) {
    const codes = props.promo.codes || [];
    if (props.user && codes.length === 0 && !props.checkout) props.loadPromos();
  }

  _track(message, props) {
    // Banner is sometimes hidden on smaller screens
    if (!this.bannerRef || !this.bannerRef.clientHeight) return;

    const map = {
      noAddress: 'no address',
      outsideDelivery: 'outside delivery',
      hasPromo: 'promo available',
      newCustomer: 'new user',
      shippingOnly: 'shipping',
      onDemandAvailable: 'on-demand'
    };

    trackNow('Site Banner Viewed', {
      banner: map[message],
      checkout: props.checkout
    });
  }

  _hasChanged(prevProps, prevState) {
    return (
      !isEqual(prevProps.address, this.props.address) ||
      prevProps.vendor !== this.props.vendor ||
      prevProps.checkoutVendor !== this.props.checkoutVendor ||
      prevProps.user !== this.props.user ||
      prevProps.promo !== this.props.promo ||
      prevProps.leadCaptureClosedAt !== this.props.leadCaptureClosedAt ||
      prevState.timerExpired !== this.state.timerExpired
    );
  }

  userRegisteredAt(leadCaptureClosedAt, user) {
    if (!leadCaptureClosedAt && typeof window !== 'undefined') {
      leadCaptureClosedAt = store.get(LEAD_CAPTURE_CLOSED_AT_KEY);
    }
    const userCreatedAt = user && +new Date(user.createdAt);
    if (leadCaptureClosedAt && userCreatedAt) {
      return leadCaptureClosedAt < userCreatedAt
        ? leadCaptureClosedAt
        : userCreatedAt;
    } else if (leadCaptureClosedAt) {
      return leadCaptureClosedAt;
    } else if (userCreatedAt) {
      return userCreatedAt;
    }
  }

  availableCredit(promo = {}) {
    const hasValidType = ({ value_type }) =>
      ['credit', '$', '%', 'sku'].indexOf(value_type) > -1;

    const { credit, codes } = promo;
    if (credit && credit.value && hasValidType(credit)) return credit;

    const code = firstPromoToApply(codes);
    if (code && code.value && hasValidType(code)) return code;
  }

  render() {
    let message = this.state.message;
    if (!message) return null;

    let expiresAt;
    if (message === 'newCustomer') {
      expiresAt = new Date(
        this.userRegisteredAt(this.props.leadCaptureClosedAt, this.props.user) +
          TIMER_LENGTH
      );
    }

    let credit = {};
    if (message === 'hasPromo') {
      credit = this.availableCredit(this.props.promo);
    }

    return (
      <DarkBackground
        className={classnames(styles.banner, this.props.className, {
          [styles.accent]: message === 'outsideDelivery'
        })}
        ref={el => (this.bannerRef = el)}
      >
        <Message inCondition={message === 'noAddress'}>
          <TextButton noUnderline onClick={() => this.props.showAvailability()}>
            Set your delivery address to see local pricing and delivery options.
          </TextButton>
        </Message>

        <Message
          inCondition={message === 'outsideDelivery'}
          className={sansSerifMedium}
        >
          Uh oh, looks like we don{"'"}t deliver to that address.{' '}
          {!this.props.checkout && (
            <>
              <TextButton onClick={() => this.props.showAvailability()}>
                Click here
              </TextButton>{' '}
              to enter a new one.
            </>
          )}
        </Message>

        <Message
          inCondition={message === 'hasPromo' && credit.value_type === 'credit'}
        >
          You have{' '}
          <span className={sansSerifMedium}>
            ${credit.balance} in your account.{' '}
          </span>
          Checkout now!
        </Message>

        <Message
          inCondition={message === 'hasPromo' && credit.value_type === '$'}
        >
          You have{' '}
          <span className={sansSerifMedium}>
            ${credit.value} in your account.{' '}
          </span>
          Checkout now!
        </Message>

        <Message
          inCondition={message === 'hasPromo' && credit.value_type === '%'}
        >
          You have{' '}
          <span className={sansSerifMedium}>
            {credit.value * 100}% off your next order.{' '}
          </span>
          Checkout now!
        </Message>

        <Message
          inCondition={message === 'hasPromo' && credit.value_type === 'sku'}
        >
          You have a promo code:{' '}
          <span className={sansSerifMedium}>{credit.desc}. </span>
          Checkout now!
        </Message>

        <Message inCondition={message === 'newCustomer'}>
          Order within{' '}
          <Timer
            expiresAt={expiresAt}
            handleExpired={() => this.setState({ timerExpired: true })}
          />{' '}
          minutes to get $5 off (Use code: DRINK5)
        </Message>

        <Message inCondition={message === 'shippingOnly'}>
          Order ${config.shipping.freeThreshold}+ &amp; get free ground shipping
        </Message>

        <Message inCondition={message === 'onDemandAvailable'}>
          No delivery fees &amp; no order minimums
        </Message>
      </DarkBackground>
    );
  }
}

function Message({ inCondition, className, children }) {
  return (
    <CSSTransition
      classNames={{
        enter: styles.msgEnter,
        enterActive: styles.msgEnterActive,
        exit: styles.msgExit,
        exitActive: styles.msgExitActive
      }}
      in={inCondition}
      mountOnEnter
      unmountOnExit
      timeout={500}
    >
      <SmallUppercase tag="div" className={className}>
        {children}
      </SmallUppercase>
    </CSSTransition>
  );
}

const ConnectedBanner = connect(
  state => ({
    address: state.address,
    vendor: state.address.vendor,
    loadingVendor: state.address.loadingVendor,
    checkoutVendor: state.checkout.vendor,
    promo: state.promo,
    user: state.login.user,
    leadCaptureClosedAt: state.login.leadCaptureClosedAt
  }),
  { loadPromos, showAvailability }
)(Banner);

export default ConnectedBanner;
